// Tokens live in sessionStorage (not localStorage) so they don't outlive the tab.
const ACCESS_TOKEN_KEY = "PUZZEL_ACCESS_TOKEN";
const ID_TOKEN_KEY = "PUZZEL_ID_TOKEN";
const REFRESH_TOKEN_KEY = "PUZZEL_REFRESH_TOKEN";
const EXPIRES_AT_KEY = "PUZZEL_TOKEN_EXPIRES_AT";
const CUSTOMER_KEY_KEY = "PUZZEL_CUSTOMER_KEY";
const PUZZEL_USER_ID_KEY = "PUZZEL_USER_ID";
const PUZZEL_USERNAME_KEY = "PUZZEL_USERNAME";
const SESSION_ID_KEY = "PUZZEL_SESSION_ID";

// Custom claims on the access token identifying the org/agent - used to build the CC5 API URL.
const CUSTOMER_KEY_CLAIM = "urn:puzzel:cc:slid";
const PUZZEL_USER_ID_CLAIM = "urn:puzzel:cc:sluid";
const PUZZEL_USERNAME_CLAIM = "urn:puzzel:cc:sluname";
// "sid" on the id_token is used to match cross-tab logout broadcasts to this login session.
const SESSION_ID_CLAIM = "sid";

// Treat a token as expired slightly early to avoid racing a request against the real expiry.
const EXPIRY_LEEWAY_MS = 30_000;

// Best-effort - Puzzel may issue opaque tokens instead of JWTs, in which case this returns null.
export const tryDecodeJwtClaims = (
  token: string,
): Record<string, unknown> | null => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    // atob gives a Latin-1 string; re-encode via percent-escapes so multi-byte UTF-8 (æøå) survives.
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((byte) => `%${byte.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export type PuzzelTokens = {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresInSeconds: number;
};

export const savePuzzelTokens = ({
  accessToken,
  idToken,
  refreshToken,
  expiresInSeconds,
}: PuzzelTokens): void => {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  sessionStorage.setItem(
    EXPIRES_AT_KEY,
    String(Date.now() + expiresInSeconds * 1000),
  );
  if (idToken) {
    sessionStorage.setItem(ID_TOKEN_KEY, idToken);
  }
  if (refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  const claims = tryDecodeJwtClaims(accessToken);
  const customerKey = claims?.[CUSTOMER_KEY_CLAIM];
  if (typeof customerKey === "string") {
    sessionStorage.setItem(CUSTOMER_KEY_KEY, customerKey);
  }
  const puzzelUserId = claims?.[PUZZEL_USER_ID_CLAIM];
  if (typeof puzzelUserId === "string") {
    sessionStorage.setItem(PUZZEL_USER_ID_KEY, puzzelUserId);
  }
  const puzzelUsername = claims?.[PUZZEL_USERNAME_CLAIM];
  if (typeof puzzelUsername === "string") {
    sessionStorage.setItem(PUZZEL_USERNAME_KEY, puzzelUsername);
  }

  // sid is on the id_token, not the access token.
  const idTokenClaims = idToken ? tryDecodeJwtClaims(idToken) : null;
  const sessionId = idTokenClaims?.[SESSION_ID_CLAIM];
  if (typeof sessionId === "string") {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
};

export const clearPuzzelTokens = (): void => {
  [
    ACCESS_TOKEN_KEY,
    ID_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    EXPIRES_AT_KEY,
    CUSTOMER_KEY_KEY,
    PUZZEL_USER_ID_KEY,
    PUZZEL_USERNAME_KEY,
    SESSION_ID_KEY,
  ].forEach((key) => sessionStorage.removeItem(key));
};

export const getValidPuzzelAccessToken = (): string | null => {
  const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAt = Number(sessionStorage.getItem(EXPIRES_AT_KEY));

  if (!accessToken || !expiresAt || Date.now() > expiresAt - EXPIRY_LEEWAY_MS) {
    return null;
  }
  return accessToken;
};

export const getPuzzelRefreshToken = (): string | null =>
  sessionStorage.getItem(REFRESH_TOKEN_KEY);

// Derived from the access token's own claims, not a hardcoded/configured value.
export const getPuzzelCustomerKey = (): string | null =>
  sessionStorage.getItem(CUSTOMER_KEY_KEY);

export const getPuzzelUserId = (): string | null =>
  sessionStorage.getItem(PUZZEL_USER_ID_KEY);

export const getPuzzelUsername = (): string | null =>
  sessionStorage.getItem(PUZZEL_USERNAME_KEY);

export const getPuzzelSessionId = (): string | null =>
  sessionStorage.getItem(SESSION_ID_KEY);

export const getPuzzelIdToken = (): string | null =>
  sessionStorage.getItem(ID_TOKEN_KEY);

const LOGOUT_BROADCAST_KEY = "PUZZEL_LOGOUT_BROADCAST";

export type PuzzelLogoutBroadcast = {
  issuer: string | null;
  sessionId: string | null;
};

export const broadcastPuzzelLogout = (payload: PuzzelLogoutBroadcast): void => {
  localStorage.setItem(LOGOUT_BROADCAST_KEY, JSON.stringify(payload));
};

// Calls `onLogout` whenever another tab broadcasts a logout. Returns an unsubscribe function.
export const subscribeToPuzzelLogoutBroadcast = (
  onLogout: (payload: PuzzelLogoutBroadcast) => void,
): (() => void) => {
  const handleStorageEvent = (event: StorageEvent) => {
    if (event.key === LOGOUT_BROADCAST_KEY && event.newValue) {
      try {
        onLogout(JSON.parse(event.newValue));
      } catch {
        // Ignore malformed broadcast payloads.
      }
    }
  };
  window.addEventListener("storage", handleStorageEvent);
  return () => window.removeEventListener("storage", handleStorageEvent);
};

// Guards against infinite redirect loops when a fresh token still gets 401'd (e.g. wrong scope).
const LOGIN_ATTEMPTS_KEY = "PUZZEL_LOGIN_ATTEMPTS";
const LOGIN_ATTEMPTS_WINDOW_START_KEY = "PUZZEL_LOGIN_ATTEMPTS_WINDOW_START";
const MAX_AUTO_LOGIN_ATTEMPTS = 2;
const ATTEMPTS_WINDOW_MS = 60_000;

// Returns false once the attempt budget is used up, meaning the caller should stop retrying.
export const registerPuzzelLoginAttempt = (): boolean => {
  const windowStart = Number(
    sessionStorage.getItem(LOGIN_ATTEMPTS_WINDOW_START_KEY) ?? "0",
  );
  const windowExpired =
    !windowStart || Date.now() - windowStart > ATTEMPTS_WINDOW_MS;

  if (windowExpired) {
    sessionStorage.setItem(LOGIN_ATTEMPTS_WINDOW_START_KEY, String(Date.now()));
    sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, "1");
    return true;
  }

  const attempts =
    Number(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) ?? "0") + 1;
  sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, String(attempts));
  return attempts <= MAX_AUTO_LOGIN_ATTEMPTS;
};

// Only call once an API call actually succeeds - proves the token is valid, not just present.
export const resetPuzzelLoginAttempts = (): void => {
  sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
  sessionStorage.removeItem(LOGIN_ATTEMPTS_WINDOW_START_KEY);
};
